import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, Users, ShoppingBag, TrendingUp, LogOut, Settings, 
  Plus, Edit2, Trash2, Upload, Save, X, ChevronDown, ChevronUp,
  Copy, Check, Eye, EyeOff, RefreshCw, Download, Mail, Search,
  Filter, MoreVertical, ArrowLeft, Image, GripVertical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Login fehlgeschlagen');
      
      localStorage.setItem('admin_token', data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="w-full max-w-md p-8 rounded-2xl" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black mb-2" style={{ 
            background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Fancy Donuts
          </h1>
          <p className="text-gray-400">Shop Admin</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white focus:border-cyan-500 focus:outline-none"
              placeholder="admin@fancy-donuts.shop"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white focus:border-cyan-500 focus:outline-none"
              placeholder="••••••••"
              required
            />
          </div>
          
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
          >
            {loading ? 'Wird angemeldet...' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Product Editor Modal
const ProductEditor = ({ product, onSave, onClose, token }) => {
  const [formData, setFormData] = useState(product || {
    name: '',
    category: 'Classics',
    price: 3.00,
    image: '',
    basis: 'Donut (Groß)',
    glasur: '',
    topping: '',
    sauce: '',
    vegan: false,
    description: '',
    active: true
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const categories = ['Classics', 'Specials', 'Dossants', 'Berliner', 'Boxen', 'Vegan'];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadData
      });
      const data = await res.json();
      if (res.ok) {
        setFormData(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-800" style={{ backgroundColor: '#111' }}>
          <h2 className="text-xl font-bold text-white">
            {product ? 'Produkt bearbeiten' : 'Neues Produkt'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Produktbild</label>
            <div className="flex items-start gap-4">
              {formData.image && (
                <img src={formData.image} alt="Preview" className="w-24 h-24 rounded-lg object-cover" />
              )}
              <div className="flex-1">
                <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 cursor-pointer hover:bg-gray-700 transition-colors">
                  <Upload className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                  <span className="text-white">{uploading ? 'Wird hochgeladen...' : 'Bild hochladen'}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                <p className="text-xs text-gray-500 mt-2">Oder URL eingeben:</p>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-black/50 border border-gray-700 text-white text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
          
          {/* Name & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Kategorie *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Price & Vegan */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Preis (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                required
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/50 border border-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.vegan}
                  onChange={(e) => setFormData(prev => ({ ...prev, vegan: e.target.checked }))}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white">Vegan</span>
              </label>
            </div>
          </div>
          
          {/* Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Basis</label>
              <input
                type="text"
                value={formData.basis || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, basis: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                placeholder="z.B. Donut (Groß)"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Glasur</label>
              <input
                type="text"
                value={formData.glasur || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, glasur: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                placeholder="z.B. Dunkle Schokolade"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Topping</label>
              <input
                type="text"
                value={formData.topping || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, topping: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                placeholder="z.B. Krokant, Streusel"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Sauce</label>
              <input
                type="text"
                value={formData.sauce || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, sauce: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white"
                placeholder="z.B. Karamell"
              />
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Beschreibung</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-black/50 border border-gray-700 text-white resize-none"
              rows={3}
              placeholder="Produktbeschreibung..."
            />
          </div>
          
          {/* Active */}
          <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/50 border border-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
              className="w-5 h-5 rounded"
            />
            <span className="text-white">Aktiv (im Shop sichtbar)</span>
          </label>
          
          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold text-gray-400 bg-gray-800 hover:bg-gray-700"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl font-bold text-white disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
            >
              {saving ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Order Details Modal
const OrderDetails = ({ order, onClose, onCopyAddress, token, onRefresh }) => {
  const [copied, setCopied] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  
  const handleCopy = () => {
    onCopyAddress(order);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvoice = async () => {
    if (!window.confirm(`Rechnung an ${order.user_email} senden?`)) return;
    setSendingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${order.id}/send-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
        onRefresh();
      } else {
        alert('Fehler: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${order.id}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung_${order.id.slice(-8)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const statusColors = {
    pending: '#FFD16F',
    paid: '#81CBD6',
    processing: '#F39DB2',
    shipped: '#4ade80',
    delivered: '#22c55e',
    cancelled: '#ef4444'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-800" style={{ backgroundColor: '#111' }}>
          <h2 className="text-xl font-bold text-white">Bestellung #{order.id.slice(-8)}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Status & Date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ 
                backgroundColor: `${statusColors[order.status] || '#888'}20`,
                color: statusColors[order.status] || '#888'
              }}>
                {order.status?.toUpperCase()}
              </span>
            </div>
            <span className="text-gray-400 text-sm">
              {new Date(order.created_at).toLocaleDateString('de-DE', { 
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </span>
          </div>
          
          {/* Customer Info */}
          <div className="p-4 rounded-xl bg-black/30 border border-gray-800">
            <h3 className="font-bold text-white mb-3">Kunde</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Firma:</span> <span className="text-white">{order.company_name || '-'}</span></p>
              <p><span className="text-gray-400">Kontakt:</span> <span className="text-white">{order.contact_name || '-'}</span></p>
              <p><span className="text-gray-400">E-Mail:</span> <span className="text-white">{order.user_email || '-'}</span></p>
            </div>
          </div>
          
          {/* Delivery Address */}
          <div className="p-4 rounded-xl bg-black/30 border border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white">Lieferadresse</h3>
              <button 
                onClick={handleCopy}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: copied ? '#22c55e20' : '#81CBD620', color: copied ? '#22c55e' : BRAND.turquoise }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <p className="text-white whitespace-pre-line">{order.delivery_address || '-'}</p>
          </div>
          
          {/* Order Items */}
          <div className="p-4 rounded-xl bg-black/30 border border-gray-800">
            <h3 className="font-bold text-white mb-3">Bestellte Produkte</h3>
            <div className="space-y-3">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${BRAND.pink}20`, color: BRAND.pink }}>
                      {item.quantity}x
                    </span>
                    <span className="text-white">{item.name}</span>
                  </div>
                  <span className="text-white font-bold">{(item.price * item.quantity).toFixed(2)} €</span>
                </div>
              ))}
            </div>
            
            {/* Total */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Zwischensumme</span>
                <span className="text-white">{order.total?.toFixed(2)} €</span>
              </div>
              {order.discount_percent > 0 && (
                <div className="flex items-center justify-between text-green-400">
                  <span>Rabatt ({order.discount_percent}%)</span>
                  <span>-{(order.total * order.discount_percent / 100).toFixed(2)} €</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-2 text-lg font-bold">
                <span className="text-white">Gesamt</span>
                <span style={{ color: BRAND.turquoise }}>{order.final_total?.toFixed(2)} €</span>
              </div>
            </div>
          </div>
          
          {/* Invoice Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
              {downloadingInvoice ? 'Wird erstellt...' : 'Rechnung PDF'}
            </button>
            <button
              onClick={handleSendInvoice}
              disabled={sendingInvoice || !order.user_email}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-white disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
            >
              <Mail className="w-5 h-5" />
              {sendingInvoice ? 'Wird gesendet...' : order.invoice_sent ? 'Erneut senden' : 'Rechnung senden'}
            </button>
          </div>
          {order.invoice_sent && (
            <p className="text-center text-sm text-green-400">
              Rechnung wurde bereits gesendet {order.invoice_number && `(${order.invoice_number})`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductEditor, setShowProductEditor] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch all data in parallel
      const [ordersRes, productsRes, usersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/orders`, { headers }),
        fetch(`${API_URL}/api/admin/products`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/stats`, { headers })
      ]);
      
      if (!ordersRes.ok || !productsRes.ok) {
        if (ordersRes.status === 401 || productsRes.status === 401) {
          localStorage.removeItem('admin_token');
          setToken(null);
          return;
        }
      }
      
      const [ordersData, productsData, usersData, statsData] = await Promise.all([
        ordersRes.json(),
        productsRes.json(),
        usersRes.json(),
        statsRes.json()
      ]);
      
      setOrders(ordersData.orders || []);
      setProducts(productsData.products || []);
      setUsers(usersData.users || []);
      setStats(statsData);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  const handleCopyAddress = (order) => {
    const address = `${order.company_name || ''}\n${order.contact_name || ''}\n${order.delivery_address || ''}`;
    navigator.clipboard.writeText(address);
  };

  const handleImportMockProducts = async () => {
    if (!window.confirm('Möchtest du alle Produkte aus der mock.js Datei importieren? Dies sollte nur einmal gemacht werden.')) return;
    
    try {
      // Import mock products from frontend
      const { donutProducts } = await import('../data/mock');
      
      const productsToImport = donutProducts.map(p => ({
        name: p.name,
        category: p.category,
        price: p.price,
        image: p.image,
        basis: p.basis || null,
        glasur: p.glasur || null,
        topping: p.topping || null,
        sauce: p.sauce || null,
        vegan: p.vegan || false,
        description: p.description || null,
        active: true
      }));
      
      const res = await fetch(`${API_URL}/api/admin/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productsToImport)
      });
      
      const data = await res.json();
      if (res.ok) {
        alert(`${data.imported_count} Produkte erfolgreich importiert!`);
        fetchData();
      } else {
        alert('Import fehlgeschlagen: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Import error:', err);
      alert('Import fehlgeschlagen: ' + err.message);
    }
  };

  const handleSaveProduct = async (productData) => {
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    try {
      if (productData.id) {
        // Update existing
        await fetch(`${API_URL}/api/admin/products/${productData.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(productData)
        });
      } else {
        // Create new
        await fetch(`${API_URL}/api/admin/products`, {
          method: 'POST',
          headers,
          body: JSON.stringify(productData)
        });
      }
      
      setShowProductEditor(false);
      setEditingProduct(null);
      fetchData();
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  if (!token) {
    return <AdminLogin onLogin={setToken} />;
  }

  const filteredOrders = orders.filter(o => 
    o.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-64 p-4 border-r border-gray-800" style={{ backgroundColor: '#0f0f0f' }}>
        <div className="mb-8">
          <h1 className="text-xl font-black" style={{ 
            background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Fancy Donuts
          </h1>
          <p className="text-gray-500 text-sm">Shop Admin</p>
        </div>
        
        <nav className="space-y-2">
          {[
            { id: 'orders', icon: ShoppingBag, label: 'Bestellungen', count: orders.length },
            { id: 'products', icon: Package, label: 'Produkte', count: products.length },
            { id: 'users', icon: Users, label: 'Kunden', count: users.length },
            { id: 'stats', icon: TrendingUp, label: 'Statistiken' },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" style={{ color: activeTab === item.id ? BRAND.turquoise : '#888' }} />
              <span className={activeTab === item.id ? 'text-white font-medium' : 'text-gray-400'}>{item.label}</span>
              {item.count !== undefined && (
                <span className="ml-auto px-2 py-0.5 rounded-full text-xs" style={{ 
                  backgroundColor: `${BRAND.turquoise}20`, 
                  color: BRAND.turquoise 
                }}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Abmelden</span>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white capitalize">
              {activeTab === 'orders' && 'Bestellungen'}
              {activeTab === 'products' && 'Produkte'}
              {activeTab === 'users' && 'Kunden'}
              {activeTab === 'stats' && 'Statistiken'}
            </h2>
            <p className="text-gray-400">
              {activeTab === 'orders' && `${orders.length} Bestellungen insgesamt`}
              {activeTab === 'products' && `${products.length} Produkte im Katalog`}
              {activeTab === 'users' && `${users.length} registrierte Kunden`}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-xl bg-white/5 border border-gray-800 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
              />
            </div>
            
            {/* Refresh */}
            <button
              onClick={fetchData}
              className="p-2 rounded-xl bg-white/5 border border-gray-800 hover:bg-white/10 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Export for Accountant */}
            {activeTab === 'orders' && (
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/admin/export/accounting`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Buchhaltung_Export_${new Date().toISOString().slice(0,10)}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (err) {
                    alert('Export fehlgeschlagen: ' + err.message);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-green-600 hover:bg-green-700"
              >
                <Download className="w-5 h-5" />
                Export für Steuerberater
              </button>
            )}
            
            {/* Add Product */}
            {activeTab === 'products' && (
              <>
                {products.length === 0 && (
                  <button
                    onClick={handleImportMockProducts}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600"
                  >
                    <Download className="w-5 h-5" />
                    Mock-Daten importieren
                  </button>
                )}
                <button
                  onClick={() => { setEditingProduct(null); setShowProductEditor(true); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                >
                  <Plus className="w-5 h-5" />
                  Neues Produkt
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin" style={{ color: BRAND.turquoise }} />
          </div>
        ) : (
          <>
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">Keine Bestellungen gefunden</div>
                ) : (
                  filteredOrders.map(order => (
                    <div 
                      key={order.id}
                      className="p-4 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors cursor-pointer"
                      style={{ backgroundColor: '#111' }}
                      onClick={() => setSelectedOrder(order)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="font-bold text-white">#{order.id.slice(-8)}</p>
                            <p className="text-sm text-gray-400">{order.company_name || order.user_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <select
                            value={order.status}
                            onChange={(e) => { e.stopPropagation(); handleUpdateOrderStatus(order.id, e.target.value); }}
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-1.5 rounded-lg bg-black/50 border border-gray-700 text-sm text-white"
                          >
                            <option value="pending">Ausstehend</option>
                            <option value="paid">Bezahlt</option>
                            <option value="processing">In Bearbeitung</option>
                            <option value="shipped">Versandt</option>
                            <option value="delivered">Geliefert</option>
                            <option value="cancelled">Storniert</option>
                          </select>
                          <span className="text-lg font-bold" style={{ color: BRAND.turquoise }}>
                            {order.final_total?.toFixed(2)} €
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-400">
                    Keine Produkte in der Datenbank. 
                    <button 
                      onClick={() => { setEditingProduct(null); setShowProductEditor(true); }}
                      className="ml-2 text-cyan-400 hover:underline"
                    >
                      Erstes Produkt hinzufügen
                    </button>
                  </div>
                ) : (
                  filteredProducts.map(product => (
                    <div 
                      key={product.id}
                      className="p-4 rounded-xl border border-gray-800"
                      style={{ backgroundColor: '#111' }}
                    >
                      <div className="flex items-start gap-4">
                        {product.image && (
                          <img src={product.image} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-bold text-white truncate">{product.name}</h3>
                              <p className="text-sm text-gray-400">{product.category}</p>
                            </div>
                            <span className="font-bold" style={{ color: BRAND.turquoise }}>{product.price?.toFixed(2)} €</span>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            {product.vegan && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#22c55e20', color: '#22c55e' }}>
                                Vegan
                              </span>
                            )}
                            {!product.active && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">
                                Inaktiv
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-800">
                        <button
                          onClick={() => { setEditingProduct(product); setShowProductEditor(true); }}
                          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {users.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">Keine Kunden registriert</div>
                ) : (
                  users.map(user => (
                    <div 
                      key={user.id}
                      className="p-4 rounded-xl border border-gray-800"
                      style={{ backgroundColor: '#111' }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-white">{user.company_name || user.email}</p>
                          <p className="text-sm text-gray-400">{user.email}</p>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Stats Tab */}
            {activeTab === 'stats' && stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-6 rounded-xl border border-gray-800" style={{ backgroundColor: '#111' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}20` }}>
                      <ShoppingBag className="w-6 h-6" style={{ color: BRAND.turquoise }} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Gesamtumsatz</p>
                      <p className="text-2xl font-bold text-white">{stats.total_revenue?.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 rounded-xl border border-gray-800" style={{ backgroundColor: '#111' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}20` }}>
                      <Package className="w-6 h-6" style={{ color: BRAND.pink }} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Bestellungen</p>
                      <p className="text-2xl font-bold text-white">{stats.total_orders}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 rounded-xl border border-gray-800" style={{ backgroundColor: '#111' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}20` }}>
                      <Users className="w-6 h-6" style={{ color: BRAND.yellow }} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Kunden</p>
                      <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 rounded-xl border border-gray-800" style={{ backgroundColor: '#111' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                      <TrendingUp className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Ø Bestellwert</p>
                      <p className="text-2xl font-bold text-white">{stats.avg_order_value?.toFixed(2)} €</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Modals */}
      {selectedOrder && (
        <OrderDetails 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)}
          onCopyAddress={handleCopyAddress}
          token={token}
          onRefresh={fetchData}
        />
      )}
      
      {showProductEditor && (
        <ProductEditor
          product={editingProduct}
          token={token}
          onSave={handleSaveProduct}
          onClose={() => { setShowProductEditor(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
